/**
 * Relatório automático de erros do cliente.
 * - Sempre loga no console (com stack, versão do build e contexto).
 * - Envia para o endpoint `log-client-error` (tabela client_error_logs).
 */
import { supabase } from '@/integrations/supabase/client';

declare const __APP_BUILD_ID__: string | undefined;

export const BUILD_ID: string =
  typeof __APP_BUILD_ID__ === 'string' ? __APP_BUILD_ID__ : 'dev';

type DebugContext = Record<string, unknown>;

const CONTEXT_KEY = 'debug-context';

/** Guarda inputs relevantes (ex.: respostas do teste) para acompanhar o erro. */
export function setDebugContext(patch: DebugContext) {
  try {
    const current = getDebugContext();
    sessionStorage.setItem(CONTEXT_KEY, JSON.stringify({ ...current, ...patch }));
  } catch {
    /* noop */
  }
}

export function getDebugContext(): DebugContext {
  try {
    return JSON.parse(sessionStorage.getItem(CONTEXT_KEY) || '{}');
  } catch {
    return {};
  }
}

export function clearDebugContext() {
  try {
    sessionStorage.removeItem(CONTEXT_KEY);
  } catch {
    /* noop */
  }
}

const sent = new Set<string>();

export async function reportError(
  error: unknown,
  extra: { source: string; componentStack?: string; context?: DebugContext } = { source: 'manual' }
) {
  const err = error instanceof Error ? error : new Error(String(error));
  const payload = {
    build_id: BUILD_ID,
    source: extra.source,
    message: `${err.name}: ${err.message}`.slice(0, 1000),
    stack: (err.stack || '').slice(0, 8000),
    component_stack: (extra.componentStack || '').slice(0, 8000),
    url: typeof window !== 'undefined' ? window.location.href : '',
    route: typeof window !== 'undefined' ? window.location.pathname : '',
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    context: { ...getDebugContext(), ...(extra.context || {}) },
    occurred_at: new Date().toISOString(),
  };

  // eslint-disable-next-line no-console
  console.error('[error-report]', payload);

  const dedupeKey = `${payload.source}|${payload.message}|${payload.route}`;
  if (sent.has(dedupeKey)) return;
  sent.add(dedupeKey);

  try {
    await supabase.functions.invoke('log-client-error', { body: payload });
  } catch (sendError) {
    // eslint-disable-next-line no-console
    console.warn('[error-report] falha ao enviar log', sendError);
  }
}

let installed = false;

/** Captura erros globais e promises rejeitadas. */
export function installGlobalErrorReporter() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, { source: 'window.onerror' });
  });

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });
}
